import { useTicketInformation } from '@/hooks/useTicketInformation/useTicketInformation';
import { FormattedMessage, useIntl } from 'react-intl';
import { getFormattedDateTime } from '@eventbrite/legacy-infra.datetime-fns';
import { CONVERSION_BAR_MESSAGES } from '../../constants';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { getDisplayTimezone } from '@/utils/dateTime';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';

import { CheckoutButton } from '../../CheckoutButton/CheckoutButton';
import styles from './SalesStartSoonPanel.module.scss';
import { RemindMe } from './RemindMe/RemindMe';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import { shouldShowRemindMe } from './shouldShowRemindMe';

export const SalesStartSoonPanel: React.FC = () => {
  const { locale } = useIntl();
  const { isOnline, isListed, isProtected } = useBasicInfo();
  const attendeeTimezone = useAttendeeTimezone(isOnline ?? false);
  const [data, loading] = useTicketInformation();

  if (loading) {
    return (
      <div className={styles.detailsLoadingSkeleton}>
        <LoadingSkeleton height={30} width="100%" />
        <LoadingSkeleton height={30} width="100%" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const timezone = getDisplayTimezone({
    isOnlineEvent: isOnline ?? false,
    eventTimezone: data.startSalesDate?.timezone,
    attendeeTimezone,
  });
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const showTimezone = timezone !== browserTimezone;
  const startDate = getFormattedDateTime(data.startSalesDate?.utc, showTimezone, timezone, locale, 'PPp');
  const showRemindMe = shouldShowRemindMe({
    isListed: isListed ?? false,
    isProtected: isProtected ?? false,
    startSalesDateUtc: data.startSalesDate?.utc,
  });

  return (
    <div className={styles.container} data-testid="sales-start-soon-panel">
      <div className={styles.content}>
        <div className={styles.label} data-testid="sales-start-soon-panel-label">
          <FormattedMessage {...CONVERSION_BAR_MESSAGES.SALES_START_SOON} values={{ date: startDate }} />
        </div>
      </div>
      {showRemindMe ? (
        <RemindMe secondaryAction={<CheckoutButton isRemindMe />} />
      ) : (
        <div data-testid="cb-cta-area" className={styles.ctaButtonWrapper}>
          <CheckoutButton isRemindMe />
        </div>
      )}
    </div>
  );
};
